import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Star,
  Share2,
  Heart,
  ChevronLeft,
  Users,
  Home as HomeIcon,
  X,
  Shield,
  Calendar,
  MapPin,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { propertiesAPI, reviewsAPI } from '../services/api';
import { toast } from '../hooks/use-toast';
import WiFiAccessModal from '../components/WiFiAccessModal';
import BookingWidget from '../components/BookingWidget';
import MapboxMap from '../components/MapboxMap';
import { getCoordinatesFromLocation } from '../utils/locationCoordinates';
import { useFavoriteProperty } from '../hooks/useFavoriteProperty';
import { mediumUrl } from '../utils/imageUrl';
import SafeImage from '../components/SafeImage';
import { getAmenityIcon } from '../utils/amenityIcons';
import { getPropertyBadges } from '../utils/propertyBadges';
import { AMENITY_LABEL_BY_ID } from '../lib/amenities';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { FEATURE_FLAGS } from '../config';

const PropertyDetailPage = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [property, setProperty] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAllPhotos, setShowAllPhotos] = useState(false);
  const [showBookingSheet, setShowBookingSheet] = useState(false);
  // Mirror of dates picked inside BookingWidget so the mobile sticky bar
  // can preview them. `useCallback` so BookingWidget's useEffect doesn't
  // refire on every parent render.
  const [selectedDates, setSelectedDates] = useState({ checkIn: null, checkOut: null });
  const handleDatesChange = React.useCallback((dates) => setSelectedDates(dates), []);
  const { isFavorite, toggle: toggleFavorite } = useFavoriteProperty(id);
  const [showWiFiModal, setShowWiFiModal] = useState(false);

  // Get source from URL query params (students, medical, etc.)
  const searchParams = new URLSearchParams(window.location.search);
  const bookingSource = searchParams.get('source');
  
  // Extract search filters from URL parameters
  const prefilledCheckIn = searchParams.get('checkIn') ? new Date(searchParams.get('checkIn')) : null;
  const prefilledCheckOut = searchParams.get('checkOut') ? new Date(searchParams.get('checkOut')) : null;
  const prefilledAdults = parseInt(searchParams.get('adults')) || null;
  const prefilledChildren = parseInt(searchParams.get('children')) || null;

  useEffect(() => {
    fetchPropertyData();
  }, [id]);

  const fetchPropertyData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [propertyData, reviewsData] = await Promise.all([
        propertiesAPI.getById(id),
        reviewsAPI.getByPropertyId(id)
      ]);
      setProperty(propertyData);
      setReviews(reviewsData);
    } catch (err) {
      console.error('Error fetching property:', err);
      // Backend returns 404 with detail={error_code: 'listing_suspended'} when
      // an admin has suspended the listing. Surface a friendly message so
      // guests understand it's a temporary pause, not a broken link.
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.error_code === 'listing_suspended') {
        setError(detail.message || 'This listing is temporarily unavailable.');
      } else {
        setError('Failed to load property details. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-rose-500 mb-4"></div>
          <p className="text-gray-600">Loading property...</p>
        </div>
      </div>
    );
  }

  if (error || !property) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            {error || 'Property not found'}
          </h2>
          <Button onClick={() => navigate(-1)} variant="outline">
            Back
          </Button>
        </div>
      </div>
    );
  }

  const handleReserve = () => {
    toast({
      title: 'Booking confirmed!',
      description: 'Your reservation has been saved. Check your trips to see details.',
    });
  };

  const handleShare = async () => {
    const url = typeof window !== 'undefined' ? window.location.href : '';
    const title = property?.title ? `${property.title} · Homesbyli` : 'Homesbyli';
    const text = property?.description
      ? `${property.title} — ${property.location || 'Montreal'}`
      : 'Check out this place on Homesbyli';

    // Mobile / supported browsers: native share sheet
    if (typeof navigator !== 'undefined' && navigator.share) {
      try {
        await navigator.share({ title, text, url });
        return;
      } catch (err) {
        // User cancelled the native share — stay silent
        if (err?.name === 'AbortError') return;
        // Any other error falls through to clipboard
      }
    }

    // Desktop fallback: copy to clipboard
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        // Legacy fallback
        const ta = document.createElement('textarea');
        ta.value = url;
        ta.setAttribute('readonly', '');
        ta.style.position = 'absolute';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      toast({
        title: 'Link copied',
        description: 'Property link copied to your clipboard — share it anywhere!',
      });
    } catch (err) {
      toast({
        title: 'Could not copy link',
        description: url,
        variant: 'destructive',
      });
    }
  };

  const handleFavorite = () => {
    const nowFavorite = toggleFavorite();
    toast({
      title: nowFavorite ? 'Added to wishlist' : 'Removed from wishlist',
      description: nowFavorite
        ? 'Property saved to your favorites'
        : 'Property removed from your favorites',
    });
  };

  if (showAllPhotos) {
    return (
      <div className="fixed inset-0 bg-black z-50 overflow-y-auto">
        <div className="max-w-5xl mx-auto p-8">
          <Button
            onClick={() => setShowAllPhotos(false)}
            variant="ghost"
            className="mb-4 text-white hover:bg-white/20"
          >
            <X className="w-5 h-5 mr-2" />
            Close
          </Button>
          <div className="grid grid-cols-1 gap-4">
            {property.images.map((image, index) => (
              <SafeImage
                key={index}
                src={mediumUrl(image)}
                alt={`${property.title} - ${index + 1}`}
                loading={index < 2 ? 'eager' : 'lazy'}
                decoding="async"
                fetchpriority={index === 0 ? 'high' : 'low'}
                className="w-full rounded-lg"
                placeholderClassName="aspect-video rounded-lg"
              />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white pb-20">
      <div className="max-w-[1120px] mx-auto px-4 sm:px-10 lg:px-20 py-4 sm:py-6 pb-28 lg:pb-6">
        {/* Back Button */}
        <Button
          onClick={() => navigate(-1)}
          variant="ghost"
          className="mb-4 -ml-3 hover:bg-gray-100"
        >
          <ChevronLeft className="w-5 h-5 mr-1" />
          Back
        </Button>

        {/* Title and Actions */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-4 sm:mb-6 gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-semibold text-gray-900 mb-2">{property.title}</h1>

            {/* Perk/structure badges — off-white pills with navy text */}
            {getPropertyBadges(property).length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3" data-testid="property-badges">
                {getPropertyBadges(property).map((b) => (
                  <span
                    key={b.key}
                    className="inline-flex items-center text-xs sm:text-sm font-semibold px-3 py-1 rounded-full border"
                    style={{
                      backgroundColor: '#F8F5EE',
                      color: '#1E3F70',
                      borderColor: 'rgba(30, 63, 112, 0.20)',
                    }}
                    data-testid={`property-badge-${b.key}`}
                  >
                    {b.label}
                  </span>
                ))}
              </div>
            )}
            
            {/* Quick Discount Badge (if available) */}
            {((property.discount_1_nights > 0 && property.discount_1_percentage > 0) || 
              (property.discount_2_nights > 0 && property.discount_2_percentage > 0)) && (
              <div className="inline-flex items-center gap-2 px-3 py-1 mb-3 bg-green-100 border border-green-300 rounded-full text-sm font-medium text-green-800">
                <span>💰</span>
                <span>
                  {property.discount_2_nights > 0 && property.discount_2_percentage > 0 
                    ? `Save up to ${property.discount_2_percentage}% on longer stays` 
                    : `Save ${property.discount_1_percentage}% on ${property.discount_1_nights}+ night stays`}
                </span>
              </div>
            )}
            
            <div className="flex items-center gap-4 text-sm">
              <div className="flex items-center gap-1">
                <Star className="w-4 h-4 fill-current" />
                <span className="font-semibold">{property.rating}</span>
                <span className="text-gray-600">·</span>
                <span className="underline cursor-pointer hover:text-gray-900">
                  {property.reviews_count} {t('property.reviews').toLowerCase()}
                </span>
              </div>
              <span className="text-gray-600">·</span>
              <span 
                className="flex items-center gap-1 cursor-pointer hover:text-gray-900"
                onClick={() => document.getElementById('map-section')?.scrollIntoView({ behavior: 'smooth' })}
                title={t('property.whereYoullBe')}
              >
                📍 {property.location}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={handleShare}
              variant="ghost"
              className="flex items-center gap-2 hover:bg-gray-100 rounded-lg"
            >
              <Share2 className="w-4 h-4" />
              <span className="underline">{t('property.shareProperty')}</span>
            </Button>
            <Button
              onClick={handleFavorite}
              variant="ghost"
              className="flex items-center gap-2 hover:bg-gray-100 rounded-lg"
            >
              <Heart
                className={`w-4 h-4 ${
                  isFavorite ? 'fill-rose-500 text-rose-500' : ''
                }`}
              />
              <span className="underline">{t('property.saveToWishlist')}</span>
            </Button>
          </div>
        </div>

        {/* Photo Grid — single full-width hero on mobile, 4-col grid on lg+ */}
        <div className="md:hidden mb-6 -mx-4 rounded-none overflow-hidden">
          <div
            className="relative aspect-[4/3] cursor-pointer"
            onClick={() => setShowAllPhotos(true)}
          >
            <SafeImage
              src={mediumUrl(property.images[0])}
              alt={property.title}
              loading="eager"
              decoding="async"
              fetchpriority="high"
              className="w-full h-full object-cover"
              placeholderClassName="w-full h-full"
            />
            {property.images.length > 1 && (
              <span className="absolute bottom-3 right-3 bg-black/60 text-white text-xs font-medium px-3 py-1.5 rounded-full backdrop-blur-sm">
                1 / {property.images.length}
              </span>
            )}
          </div>
        </div>
        <div className="hidden md:grid grid-cols-4 gap-2 mb-12 rounded-xl overflow-hidden h-[480px]">
          <div
            className="col-span-2 row-span-2 cursor-pointer relative group"
            onClick={() => setShowAllPhotos(true)}
          >
            <SafeImage
              src={mediumUrl(property.images[0])}
              alt={property.title}
              loading="eager"
              decoding="async"
              fetchpriority="high"
              className="w-full h-full object-cover transition-transform group-hover:scale-105"
              placeholderClassName="w-full h-full"
            />
          </div>
          {property.images.slice(1, 5).map((image, index) => (
            <div
              key={index}
              className="cursor-pointer relative group overflow-hidden"
              onClick={() => setShowAllPhotos(true)}
            >
              <SafeImage
                src={mediumUrl(image)}
                alt={`${property.title} - ${index + 2}`}
                loading="eager"
                decoding="async"
                className="w-full h-full object-cover transition-transform group-hover:scale-105"
                placeholderClassName="w-full h-full"
              />
              {index === 3 && property.images.length > 5 && (
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                  <span className="text-white font-semibold">+{property.images.length - 5} more</span>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-16">
          {/* Left Content */}
          <div className="lg:col-span-2">
            {/* Host Info */}
            <div className="pb-8 border-b border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-2xl font-semibold mb-2 flex items-center flex-wrap gap-2">
                    Entire place hosted by{' '}
                    {property.host_id ? (
                      <span data-testid="host-name-link">{property.host.name}</span>
                    ) : (
                      <span>{property.host.name}</span>
                    )}
                    {property.host_trust?.host_verified && (
                      <span
                        data-testid="verified-host-badge"
                        title="ID verified · Government ID confirmed by Stripe Identity"
                        className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-50 text-blue-800 border border-blue-200"
                      >
                        <Shield className="w-3.5 h-3.5" />
                        Verified host
                      </span>
                    )}
                    {property.host_trust?.is_trusted && (
                      <span
                        data-testid="trusted-host-badge"
                        title={`Trusted host · ${property.host_trust.months_active}+ months · ${property.host_trust.review_count}+ reviews`}
                        className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-800 border border-emerald-200"
                      >
                        <Shield className="w-3.5 h-3.5" />
                        Trusted host
                      </span>
                    )}
                  </h2>
                  <div className="flex items-center gap-2 text-gray-600">
                    <span>{property.guests} guests</span>
                    <span>·</span>
                    <span>{property.bedrooms} bedrooms</span>
                    <span>·</span>
                    <span>{property.beds} beds</span>
                    <span>·</span>
                    <span>{property.baths} baths</span>
                  </div>
                  {property.minimum_nights && property.minimum_nights > 1 && (
                    <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-full text-sm text-blue-700 font-medium">
                      <Calendar className="w-4 h-4" />
                      Minimum stay: {property.minimum_nights} nights
                    </div>
                  )}
                  
                  {/* Pet Friendly Badge */}
                  {property.pet_friendly && (
                    <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 bg-amber-50 border border-amber-300 rounded-full text-sm text-amber-800 font-medium ml-2">
                      <span className="text-base">🐾</span>
                      Pet Friendly
                      {property.pet_fee > 0 && (
                        <span className="text-xs text-amber-600">(+CA${property.pet_fee} fee)</span>
                      )}
                    </div>
                  )}
                  
                  {/* Length-of-Stay Discounts - HIDE on Student/Medical pages (they have package deals instead) */}
                  {bookingSource !== 'students' && bookingSource !== 'medical' && 
                   ((property.discount_1_nights > 0 && property.discount_1_percentage > 0) || 
                    (property.discount_2_nights > 0 && property.discount_2_percentage > 0)) && (
                    <div className="mt-4 p-4 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg">
                      <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                        <span className="text-lg">💰</span>
                        Special Offers - Length-of-Stay Discounts
                      </h3>
                      <div className="space-y-2">
                        {property.discount_1_nights > 0 && property.discount_1_percentage > 0 && (
                          <div className="flex items-center gap-2 text-sm">
                            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                            <span className="font-medium text-green-700">
                              {property.discount_1_percentage}% off for {property.discount_1_nights}+ night stays
                            </span>
                          </div>
                        )}
                        {property.discount_2_nights > 0 && property.discount_2_percentage > 0 && (
                          <div className="flex items-center gap-2 text-sm">
                            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                            <span className="font-medium text-green-700">
                              {property.discount_2_percentage}% off for {property.discount_2_nights}+ night stays
                            </span>
                          </div>
                        )}
                      </div>
                      <p className="text-xs text-gray-600 mt-2">
                        💡 Discount automatically applied when you book {property.discount_1_nights || property.discount_2_nights}+ nights
                      </p>
                    </div>
                  )}
                </div>
                {property.host_id ? (
                  <img
                    src={property.host.avatar}
                    alt={property.host.name}
                    className="w-14 h-14 rounded-full"
                  />
                ) : (
                  <img
                    src={property.host.avatar}
                    alt={property.host.name}
                    className="w-14 h-14 rounded-full"
                  />
                )}
              </div>
            </div>

            {/* Features */}
            <div className="py-8 border-b border-gray-200 space-y-6">
              {property.pet_friendly && (
                <div className="flex items-start gap-4">
                  <span className="text-2xl flex-shrink-0">🐾</span>
                  <div>
                    <h3 className="font-semibold mb-1">Pet Friendly</h3>
                    <p className="text-gray-600 text-sm">
                      Bring your furry friends along!
                      {property.pet_fee > 0 && ` Pet fee: CA$${property.pet_fee}`}
                    </p>
                  </div>
                </div>
              )}
              <div className="flex items-start gap-4">
                <HomeIcon className="w-6 h-6 text-gray-700 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold mb-1">Entire home</h3>
                  <p className="text-gray-600 text-sm">
                    You'll have the entire place to yourself.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <Shield className="w-6 h-6 text-gray-700 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold mb-1">Enhanced Clean</h3>
                  <p className="text-gray-600 text-sm">
                    This host has committed to Homesbyli's enhanced cleaning process.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <Calendar className="w-6 h-6 text-gray-700 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold mb-1">
                    {property.cancellation_policy === 'flexible' && 'Free cancellation up to 24 hours before check-in'}
                    {property.cancellation_policy === 'moderate' && 'Free cancellation up to 5 days before check-in'}
                    {property.cancellation_policy === 'strict' && 'Free cancellation up to 14 days before check-in'}
                    {property.cancellation_policy === 'non_refundable' && 'Non-Refundable - No cancellations'}
                    {!property.cancellation_policy && 'Free cancellation up to 5 days before check-in'}
                  </h3>
                  <p className="text-gray-600 text-sm">
                    {property.cancellation_policy === 'non_refundable' 
                      ? 'This booking cannot be cancelled. No refunds will be issued.'
                      : 'Get a full refund if you cancel before check-in.'}
                  </p>
                </div>
              </div>
            </div>

            {/* Description */}
            <div className="py-8 border-b border-gray-200">
              <p className="text-gray-700 leading-relaxed">{property.description}</p>
            </div>

            {/* Amenities */}
            <div className="py-8 border-b border-gray-200">
              <h2 className="text-2xl font-semibold mb-6">{t('property.whatThisPlaceOffers')}</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {property.amenities.map((amenity, index) => {
                  const label = AMENITY_LABEL_BY_ID[amenity] || amenity;
                  const Icon = getAmenityIcon(label);
                  return (
                    <div key={index} className="flex items-center gap-4" data-testid="amenity-row">
                      <Icon className="w-6 h-6 text-gray-700" />
                      <span className="text-gray-700 capitalize">{label}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Reviews */}
            <div className="py-8 border-b border-gray-200">
              <div className="flex items-center gap-2 mb-6">
                <Star className="w-6 h-6 fill-current" />
                <h2 className="text-2xl font-semibold">
                  {property.rating} · {property.reviews_count} {t('property.reviews').toLowerCase()}
                </h2>
              </div>
              {reviews.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {reviews.map((review) => (
                    <div key={review.id || review._id}>
                      <div className="flex items-center gap-3 mb-3">
                        <img
                          src={review.avatar}
                          alt={review.user}
                          className="w-12 h-12 rounded-full"
                        />
                        <div>
                          <p className="font-semibold">{review.user}</p>
                          <p className="text-sm text-gray-600">{review.date}</p>
                        </div>
                      </div>
                      <p className="text-gray-700 text-sm leading-relaxed">{review.comment}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-600">{t('property.noReviewsYet')}</p>
              )}
            </div>

            {/* Location Map */}
            <div id="map-section" className="py-8">
              <h2 className="text-2xl font-semibold mb-4">{t('property.whereYoullBe')}</h2>
              
              <Dialog>
                <DialogTrigger asChild>
                  <Button 
                    variant="outline" 
                    className="w-full sm:w-auto flex items-center gap-2 mb-4"
                  >
                    <MapPin className="w-4 h-4" />
                    View Map
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-4xl max-h-[90vh]">
                  <DialogHeader>
                    <DialogTitle>{property.title}</DialogTitle>
                  </DialogHeader>
                  <div className="mt-4">
                    <MapboxMap
                      {...getCoordinatesFromLocation(property.location, property.title, property.neighborhood)}
                      markerTitle={property.title}
                      markerDescription={property.location}
                      height="500px"
                    />
                  </div>
                </DialogContent>
              </Dialog>
              
              <p className="text-gray-700 mt-4">
                <strong>{property.location}</strong>
              </p>
              <p className="text-gray-600 text-sm mt-2">
                Exact location provided after booking
              </p>
            </div>
          </div>

          {/* Right Sidebar - Booking Card (desktop only) */}
          <div className="hidden lg:block lg:col-span-1">
            <BookingWidget 
              property={property} 
              bookingSource={bookingSource}
              prefilledCheckIn={prefilledCheckIn}
              prefilledCheckOut={prefilledCheckOut}
              prefilledAdults={prefilledAdults}
              prefilledChildren={prefilledChildren}
              onDatesChange={handleDatesChange}
            />
          </div>
        </div>
      </div>

      {/* Mobile Sticky Reserve Bar — visible <lg; padded for iOS home-indicator */}
      <div
        className="lg:hidden fixed bottom-0 left-0 right-0 z-30 bg-white border-t border-gray-200 px-4 py-3 pb-[calc(env(safe-area-inset-bottom)+12px)] shadow-[0_-4px_16px_rgba(0,0,0,0.06)]"
        data-testid="mobile-reserve-bar"
      >
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="text-lg font-bold text-gray-900 leading-tight">
              CA${property.price}
              <span className="text-sm font-normal text-gray-600"> /{t('property.night', 'night')}</span>
            </div>
            {selectedDates.checkIn && selectedDates.checkOut ? (
              <div
                className="text-xs text-gray-700 font-medium truncate mt-0.5"
                data-testid="mobile-reserve-bar-dates"
              >
                {selectedDates.checkIn.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                {' – '}
                {selectedDates.checkOut.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              </div>
            ) : property.rating && (
              <div className="flex items-center gap-1 text-xs text-gray-600 truncate">
                <Star className="w-3 h-3 fill-current text-amber-500" />
                {property.rating}
                {property.reviews && <span>· {property.reviews} {t('property.reviews', 'reviews')}</span>}
              </div>
            )}
          </div>
          <Button
            onClick={() => setShowBookingSheet(true)}
            className="bg-rose-500 hover:bg-rose-600 text-white font-semibold rounded-full px-6 py-3 h-auto min-h-[48px]"
            data-testid="mobile-reserve-btn"
          >
            {selectedDates.checkIn && selectedDates.checkOut
              ? (FEATURE_FLAGS.directBooking ? t('booking.reserve', 'Reserve') : 'Book via partner')
              : property.booking_mode === 'instant' && FEATURE_FLAGS.directBooking
                ? t('booking.reserve', 'Reserve')
                : t('booking.checkAvailability', 'Check availability')}
          </Button>
        </div>
      </div>

      {/* Mobile Booking Sheet — full BookingWidget in a bottom drawer.
          modal={false} is critical: the booking widget portals DatePicker
          and GuestPicker popovers into document.body, which Radix Dialog
          (modal=true) wraps in `pointer-events: none` — making the
          calendar visible but unclickable on iOS Safari. */}
      <Sheet open={showBookingSheet} onOpenChange={setShowBookingSheet} modal={false}>
        <SheetContent
          side="bottom"
          className="h-[95vh] rounded-t-2xl overflow-y-auto p-0"
          data-testid="mobile-booking-sheet"
        >
          <SheetHeader className="px-4 py-3 border-b sticky top-0 bg-white z-10">
            <SheetTitle className="text-base">
              {FEATURE_FLAGS.directBooking && property.booking_mode === 'instant' ? t('booking.reserve', 'Reserve') : t('booking.checkAvailability', 'Check availability')}
            </SheetTitle>
          </SheetHeader>
          <div className="p-4">
            <BookingWidget
              property={property}
              bookingSource={bookingSource}
              prefilledCheckIn={selectedDates.checkIn || prefilledCheckIn}
              prefilledCheckOut={selectedDates.checkOut || prefilledCheckOut}
              prefilledAdults={prefilledAdults}
              prefilledChildren={prefilledChildren}
              onDatesChange={handleDatesChange}
            />
          </div>
        </SheetContent>
      </Sheet>

      {/* WiFi Access Modal */}
      <WiFiAccessModal
        isOpen={showWiFiModal}
        onClose={() => setShowWiFiModal(false)}
        propertyId={property.id || property._id}
        propertyTitle={property.title}
      />
    </div>
  );
};

export default PropertyDetailPage;
